import { useState, useEffect } from "react";
import {
  Card,
  FormLayout,
  TextField,
  Select,
  Checkbox,
  TextContainer,
  Banner,
} from "@shopify/polaris";

interface JSONSchemaProperty {
  type: string;
  title?: string;
  description?: string;
  default?: any;
  enum?: string[];
  minimum?: number;
  maximum?: number;
}

interface JSONSchema {
  type: "object";
  title?: string;
  description?: string;
  properties: Record<string, JSONSchemaProperty>;
  required?: string[];
}

interface Props {
  schema: JSONSchema;
  initialConfig?: Record<string, any>;
  onChange: (config: Record<string, any>) => void;
}

export function GameConfigForm({ schema, initialConfig = {}, onChange }: Props) {
  const [config, setConfig] = useState<Record<string, any>>(initialConfig);

  useEffect(() => {
    // Initialize with default values from schema
    const defaults: Record<string, any> = {};
    Object.entries(schema.properties || {}).forEach(([key, prop]) => {
      if (prop.default !== undefined && config[key] === undefined) {
        defaults[key] = prop.default;
      }
    });

    if (Object.keys(defaults).length > 0) {
      const newConfig = { ...config, ...defaults };
      setConfig(newConfig);
      onChange(newConfig);
    }
  }, [schema]);

  const updateConfig = (key: string, value: any) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    onChange(newConfig);
  };

  const renderField = (key: string, prop: JSONSchemaProperty) => {
    const label = prop.title || key;
    const helpText = prop.description;
    const required = schema.required?.includes(key);

    switch (prop.type) {
      case "string":
        if (prop.enum) {
          return (
            <Select
              key={key}
              label={label}
              value={config[key] || prop.default || prop.enum[0]}
              options={prop.enum.map((value) => ({ label: value, value }))}
              onChange={(value) => updateConfig(key, value)}
              helpText={helpText}
              requiredIndicator={required}
            />
          );
        }
        return (
          <TextField
            key={key}
            label={label}
            value={config[key] || prop.default || ""}
            onChange={(value) => updateConfig(key, value)}
            helpText={helpText}
            requiredIndicator={required}
            autoComplete="off"
          />
        );

      case "number":
      case "integer":
        return (
          <TextField
            key={key}
            label={label}
            type="number"
            value={config[key]?.toString() || prop.default?.toString() || ""}
            onChange={(value) => updateConfig(key, parseFloat(value) || 0)}
            helpText={helpText}
            min={prop.minimum}
            max={prop.maximum}
            requiredIndicator={required}
            autoComplete="off"
          />
        );

      case "boolean":
        return (
          <Checkbox
            key={key}
            label={label}
            checked={config[key] !== undefined ? config[key] : prop.default || false}
            onChange={(checked) => updateConfig(key, checked)}
            helpText={helpText}
          />
        );

      default:
        return (
          <TextField
            key={key}
            label={label}
            value={JSON.stringify(config[key] || prop.default || "")}
            onChange={(value) => {
              try {
                updateConfig(key, JSON.parse(value));
              } catch {
                updateConfig(key, value);
              }
            }}
            helpText={`${helpText ? helpText + " - " : ""}Advanced: JSON value`}
            multiline
            autoComplete="off"
          />
        );
    }
  };

  const properties = schema.properties || {};
  const hasProperties = Object.keys(properties).length > 0;

  if (!hasProperties) {
    return (
      <Card>
        <div style={{ padding: "1rem" }}>
          <p style={{ fontWeight: 600, marginBottom: "0.5rem" }}>Game Configuration</p>
          <Banner>
            This game has no configurable options.
          </Banner>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ padding: "1rem" }}>
        <p style={{ fontWeight: 600, marginBottom: "1rem" }}>Game Configuration</p>
        {schema.description && (
          <TextContainer>
            <p>{schema.description}</p>
          </TextContainer>
        )}

        <div style={{ marginTop: "1rem" }}>
          <FormLayout>
            {Object.entries(properties).map(([key, prop]) => renderField(key, prop))}
          </FormLayout>
        </div>
      </div>
    </Card>
  );
}
